import { Decimal } from "decimal.js";

// Configure Decimal.js for financial calculations
Decimal.set({
  precision: 20,
  rounding: Decimal.ROUND_HALF_UP,
  toExpPos: 9999,
  toExpNeg: -9999,
});

/**
 * Creates a date in local timezone from an ISO string
 * @param {string} dateString - ISO date string
 * @returns {Date} Date object in local timezone
 */
const createLocalDate = (dateString) => {
  console.log("createLocalDate input:", dateString);

  // Parse the date string and create date in local time
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day); // months are 0-based in JS

  // Ensure midnight in local timezone
  date.setHours(0, 0, 0, 0);

  return date;
};

/**
 * Gets the number of days in a month accounting for leap years
 * @param {number} year - Full year
 * @param {number} month - Month (0-11)
 * @returns {number} Number of days in month
 */
const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

/**
 * Calculates prorated rent and next due date based on move-in date and organization settings
 * @param {string} moveInDate - ISO date string of move-in date
 * @param {number} houseFee - House rent amount (weekly or monthly)
 * @param {number} moveInFee - One-time move-in fee
 * @param {string} rentSchedule - Either 'weekly' or 'monthly'
 * @param {string} rentDueDate - ISO date string of selected rent due date
 * @returns {Object} Calculation results including balance and next due date
 * @throws {Error} If parameters are invalid
 */
export const calculateProratedRent = (
  moveInDate,
  houseFee,
  moveInFee,
  rentSchedule,
  rentDueDate // Changed from rentDueDay to rentDueDate
) => {
  // Validate all inputs first
  validateRentInputs({
    moveInDate,
    houseFee,
    moveInFee,
    rentSchedule,
    rentDueDate,
  });

  console.log("=== calculateProratedRent Inputs ===");
  console.log("moveInDate:", moveInDate);
  console.log("houseFee:", houseFee);
  console.log("moveInFee:", moveInFee);
  console.log("rentSchedule:", rentSchedule);
  console.log("rentDueDate:", rentDueDate);
  console.log("===============================");

  // Convert to Decimal instances for precise arithmetic
  const decimalHouseFee = new Decimal(houseFee);
  const decimalMoveInFee = new Decimal(moveInFee);

  // Create dates in local timezone
  const moveInDateTime = createLocalDate(moveInDate);
  const dueDateDateTime = createLocalDate(rentDueDate);

  if (rentSchedule === "weekly") {
    return calculateWeeklyRent(
      moveInDateTime,
      decimalHouseFee,
      decimalMoveInFee,
      dueDateDateTime
    );
  } else {
    return calculateMonthlyRent(
      moveInDateTime,
      decimalHouseFee,
      decimalMoveInFee,
      dueDateDateTime.getDate() // For monthly, we still just need the day of month
    );
  }
};

/**
 * Calculates weekly rent with precise decimal arithmetic
 * @private
 */
const calculateWeeklyRent = (
  moveInDateTime,
  decimalHouseFee,
  decimalMoveInFee,
  selectedDueDate
) => {
  console.log("=== Starting Weekly Rent Calculation ===");
  const localDate = new Date(moveInDateTime);
  const dueDate = new Date(selectedDueDate);

  console.log("Dates:", {
    moveIn: localDate.toISOString(),
    dueDate: dueDate.toISOString(),
  });

  // Calculate daily rate
  const decimalDailyRate = decimalHouseFee.dividedBy(7);

  // If moving in on the selected due date
  if (localDate.getTime() === dueDate.getTime()) {
    console.log("Moving in on rent due date - calculating full week");
    const nextDueDate = new Date(dueDate);
    nextDueDate.setDate(dueDate.getDate() + 7);
    nextDueDate.setHours(0, 0, 0, 0);

    const fullPeriodRentAndFee = decimalHouseFee.plus(decimalMoveInFee);
    const totalBalance = fullPeriodRentAndFee.negated();

    return {
      totalBalance: totalBalance.toDecimalPlaces(2).toNumber(),
      rentDueDate: nextDueDate,
      breakdown: {
        moveInFee: decimalMoveInFee.toNumber(),
        dailyRate: decimalDailyRate.toDecimalPlaces(2).toNumber(),
        proratedRent: decimalHouseFee.toNumber(),
        daysUntilDue: 7,
        nextDueDate,
        isFullPeriod: true,
      },
    };
  }

  // Calculate days until the selected due date
  const daysUntilDue = Math.ceil(
    (dueDate.getTime() - localDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  console.log("Days calculation:", {
    daysUntilDue,
    moveInDate: localDate.toISOString(),
    dueDate: dueDate.toISOString(),
  });

  const decimalProratedRent = decimalDailyRate.times(daysUntilDue);
  const totalBalance = decimalProratedRent.plus(decimalMoveInFee).negated();

  console.log("Final calculation:", {
    dailyRate: decimalDailyRate.toString(),
    daysUntilDue,
    proratedRent: decimalProratedRent.toString(),
    moveInFee: decimalMoveInFee.toString(),
    totalBalance: totalBalance.toString(),
  });

  return {
    totalBalance: totalBalance.toDecimalPlaces(2).toNumber(),
    rentDueDate: dueDate,
    breakdown: {
      moveInFee: decimalMoveInFee.toNumber(),
      dailyRate: decimalDailyRate.toDecimalPlaces(2).toNumber(),
      proratedRent: decimalProratedRent.toDecimalPlaces(2).toNumber(),
      daysUntilDue,
      nextDueDate: dueDate,
      isFullPeriod: false,
    },
  };
};

/**
 * Calculates monthly rent with precise decimal arithmetic
 * @private
 */
const calculateMonthlyRent = (
  moveInDateTime,
  decimalHouseFee,
  decimalMoveInFee,
  rentDueDay
) => {
  console.log("=== Starting Monthly Rent Calculation ===");
  console.log("Input Parameters:", {
    moveInDateTime,
    decimalHouseFee: decimalHouseFee.toString(),
    decimalMoveInFee: decimalMoveInFee.toString(),
    rentDueDay,
  });

  const localDate = new Date(moveInDateTime);
  const moveInDay = localDate.getDate();
  const moveInMonth = localDate.getMonth();
  const moveInYear = localDate.getFullYear();

  console.log("Parsed Move-in Date:", {
    localDate: localDate.toISOString(),
    moveInDay,
    moveInMonth,
    moveInYear,
  });

  // Calculate daily rate using weekly logic
  const weeklyEquivalent = decimalHouseFee.dividedBy(4.33); // Convert monthly back to weekly
  const decimalDailyRate = weeklyEquivalent.dividedBy(7); // Get daily rate from weekly amount

  // Still need daysInMonth for other calculations
  const daysInMonth = getDaysInMonth(moveInYear, moveInMonth);

  console.log("Daily Rate Calculation:", {
    monthlyAmount: decimalHouseFee.toString(),
    weeklyEquivalent: weeklyEquivalent.toString(),
    calculatedDailyRate: decimalDailyRate.toString(),
  });

  // If moving in on rent due day
  if (moveInDay === rentDueDay) {
    console.log("Moving in on rent due day - calculating full month");
    const nextDueDate = new Date(localDate);
    nextDueDate.setMonth(nextDueDate.getMonth() + 1);
    nextDueDate.setHours(0, 0, 0, 0);

    const fullMonthRentAndFee = decimalHouseFee.plus(decimalMoveInFee);
    const totalBalance = fullMonthRentAndFee.negated();

    console.log("Full Month Calculation:", {
      fullMonthRent: decimalHouseFee.toString(),
      moveInFee: decimalMoveInFee.toString(),
      totalBalance: totalBalance.toString(),
    });

    return {
      totalBalance: totalBalance.toDecimalPlaces(2).toNumber(),
      rentDueDate: nextDueDate,
      breakdown: {
        moveInFee: decimalMoveInFee.toNumber(),
        dailyRate: decimalDailyRate.toDecimalPlaces(2).toNumber(),
        proratedRent: decimalHouseFee.toNumber(),
        daysUntilDue: getDaysInMonth(moveInYear, moveInMonth),
        nextDueDate,
        daysInMonth,
        isFullPeriod: true,
      },
    };
  }

  // Calculate next due date
  let nextDueDate = new Date(moveInYear, moveInMonth, rentDueDay);
  console.log("Initial next due date:", nextDueDate.toISOString());

  // If the due day has passed this month, move to next month
  if (moveInDay > rentDueDay) {
    nextDueDate.setMonth(nextDueDate.getMonth() + 1);
    console.log(
      "Due day passed, moved to next month:",
      nextDueDate.toISOString()
    );
  }

  // Handle edge case where due day is greater than days in target month
  const targetMonth = nextDueDate.getMonth();
  const daysInTargetMonth = getDaysInMonth(
    nextDueDate.getFullYear(),
    targetMonth
  );

  console.log("Target month details:", {
    targetMonth,
    daysInTargetMonth,
    rentDueDay,
  });

  if (rentDueDay > daysInTargetMonth) {
    nextDueDate = new Date(
      nextDueDate.getFullYear(),
      targetMonth,
      daysInTargetMonth
    );
    console.log("Adjusted due date for month end:", nextDueDate.toISOString());
  }

  nextDueDate.setHours(0, 0, 0, 0);

  // Calculate days until next due date
  const daysUntilDue = Math.ceil(
    (nextDueDate.getTime() - localDate.getTime()) / (1000 * 60 * 60 * 24) - 1
  );

  console.log("Days calculation:", {
    daysUntilDue,
    nextDueDate: nextDueDate.toISOString(),
    moveInDate: localDate.toISOString(),
  });

  const decimalProratedRent = decimalDailyRate.times(daysUntilDue);
  const totalBalance = decimalProratedRent.plus(decimalMoveInFee).negated();

  console.log("Final calculation:", {
    dailyRate: decimalDailyRate.toString(),
    daysUntilDue,
    proratedRent: decimalProratedRent.toString(),
    moveInFee: decimalMoveInFee.toString(),
    totalBalance: totalBalance.toString(),
  });

  return {
    totalBalance: totalBalance.toDecimalPlaces(2).toNumber(),
    rentDueDate: nextDueDate,
    breakdown: {
      moveInFee: decimalMoveInFee.toNumber(),
      dailyRate: decimalDailyRate.toDecimalPlaces(2).toNumber(),
      proratedRent: decimalProratedRent.toDecimalPlaces(2).toNumber(),
      daysUntilDue,
      daysInMonth,
      nextDueDate,
      isFullPeriod: false,
    },
  };
};

/**
 * Validates all calculation inputs
 * @throws {Error} If any parameters are invalid
 */
export const validateRentInputs = ({
  moveInDate,
  houseFee,
  moveInFee,
  rentSchedule,
  rentDueDate,
}) => {
  console.log("=== validateRentInputs ===");
  console.log("moveInDate:", moveInDate);
  console.log("houseFee:", houseFee);
  console.log("moveInFee:", moveInFee);
  console.log("rentSchedule:", rentSchedule);
  console.log("rentDueDate:", rentDueDate);
  console.log("===========================");

  if (!moveInDate) {
    throw new Error("Move-in date is required");
  }

  if (!houseFee || isNaN(houseFee) || houseFee <= 0) {
    throw new Error("House fee must be a positive number");
  }

  if (
    moveInFee === undefined ||
    moveInFee === null ||
    isNaN(moveInFee) ||
    moveInFee < 0
  ) {
    throw new Error("Move-in fee must be zero or a positive number");
  }

  if (!rentSchedule || !["weekly", "monthly"].includes(rentSchedule)) {
    throw new Error('Rent schedule must be either "weekly" or "monthly"');
  }

  if (!rentDueDate) {
    throw new Error("Rent due date is required");
  }

  return true;
};
